/**
 * Import-flow (source-of-truth) configuration.
 *
 * Which system wins for each student-data area during imports. Defaults
 * match the district-standard flow; schools can override on the Import &
 * data sources setup page. Stored per-school as schools/{id}.importFlow
 * (importFlow is a school-scoped settings key).
 *
 * meetingDate is intentionally absent — held meeting dates are always
 * app/calendar-owned and never imported from SEIS.
 */

export const DEFAULT_IMPORT_FLOW_CONFIG = {
  // Source (how data gets in) — drives which wizards show and locks Flow radios.
  identitySource: 'aeries-csv', // 'aeries-csv' | 'aeries-api' | 'app'
  iepSource: 'seis', // 'seis' | 'app'

  // Flow (who wins per field)
  reviewDate: 'seis', // 'seis' | 'app'
  reevalDate: 'seis', // 'seis' | 'app'
  disability: 'seis', // 'seis' | 'app'
  caseManager: 'seis', // 'seis' | 'app'
  providers: 'seis', // 'seis' | 'app'
  allowSeisToClearProviders: false,
  identity: 'aeries', // 'aeries' | 'seis' | 'app'
  contacts: 'aeries', // 'aeries' | 'seis' | 'app'
  // Name/grade differences are not review flags unless a school opts in.
  // Aeries (or the app) still owns the stored name either way.
  identityFlagOnMismatch: false,
  contactsFlagOnMismatch: true,
  // Form locks only — they do not change who wins on import.
  lockAeriesFieldsInForm: false,
  lockSeisFieldsInForm: false
}

/** Source options — separate from Flow radios; rendered as method cards. */
export const IMPORT_SOURCE_FIELDS = [
  {
    key: 'identitySource',
    label: 'Identity & schedule',
    options: ['aeries-csv', 'aeries-api', 'app']
  },
  { key: 'iepSource', label: 'IEP & compliance', options: ['seis', 'app'] }
]

/**
 * Per-field SoT metadata — drives sanitization and the Setup UI radios.
 * School psychologist is its own provider slot (`psychId`), not mental health.
 */
export const IMPORT_FLOW_FIELDS = [
  { key: 'reviewDate', label: 'Plan review date', options: ['seis', 'app'] },
  { key: 'reevalDate', label: 'Reevaluation date', options: ['seis', 'app'] },
  { key: 'disability', label: 'Disability', options: ['seis', 'app'] },
  { key: 'caseManager', label: 'Case manager', options: ['seis', 'app'] },
  { key: 'providers', label: 'Service providers', options: ['seis', 'app'] },
  { key: 'identity', label: 'Name & grade', options: ['aeries', 'seis', 'app'] },
  { key: 'contacts', label: 'Contacts', options: ['aeries', 'seis', 'app'] }
]

const IMPORT_FLOW_TOGGLES = [
  'allowSeisToClearProviders',
  'identityFlagOnMismatch',
  'contactsFlagOnMismatch',
  'lockAeriesFieldsInForm',
  'lockSeisFieldsInForm'
]

/**
 * Keep only known keys with valid values. Unknown keys (including
 * meetingDate) and out-of-range values are dropped, so a hand-edited or
 * corrupt Firestore doc can never produce an out-of-contract config.
 */
export function sanitizeImportFlowConfig(overrides = {}) {
  if (!overrides || typeof overrides !== 'object') return {}
  const clean = {}
  for (const { key, options } of [...IMPORT_SOURCE_FIELDS, ...IMPORT_FLOW_FIELDS]) {
    if (overrides[key] === undefined) continue
    if (options.includes(overrides[key])) clean[key] = overrides[key]
  }
  for (const key of IMPORT_FLOW_TOGGLES) {
    if (typeof overrides[key] === 'boolean') clean[key] = overrides[key]
  }
  return clean
}

/** True when Source chose Aeries (CSV or API) for identity & schedule. */
export function isIdentityLockedBySource(config = {}) {
  return config.identitySource === 'aeries-csv' || config.identitySource === 'aeries-api'
}

/**
 * Merge stored overrides over the defaults. Accepts either an
 * effective-settings object (reads settings.importFlow) or a raw overrides
 * object.
 *
 * Source → Flow coherence (per the mock): choosing Aeries in Source locks
 * identity to Aeries regardless of any stored Flow radio, so a stale or
 * hand-edited identity override can never contradict the Source choice.
 */
export function resolveImportFlowConfig(settings = {}) {
  const overrides = settings?.importFlow || settings || {}
  const resolved = { ...DEFAULT_IMPORT_FLOW_CONFIG, ...sanitizeImportFlowConfig(overrides) }
  if (isIdentityLockedBySource(resolved)) resolved.identity = 'aeries'
  return resolved
}

/** True when SEIS import should be hidden in Step 2 (IEP managed in-app). */
export function isSeisImportDisabled(config = {}) {
  return config.iepSource === 'app'
}
